import React, { useRef } from "react"
import { ReportLayout } from "../../../components/reports"
import AttendeeReportCharts from "./charts"
import reportData from "../data/attendee-report-2025-data.json"
import type { ReportData } from "./reportTypes"

// Cast the imported data to the proper type
const typedReportData: ReportData = reportData as ReportData

const AttendeeContentReport = () => {
  // References for chart canvases
  const importanceChartRef = useRef<HTMLCanvasElement>(null)
  const measurementGapChartRef = useRef<HTMLCanvasElement>(null)
  const contentTypesChartRef = useRef<HTMLCanvasElement>(null)
  const platformsChartRef = useRef<HTMLCanvasElement>(null)
  const challengesSolutionsChartRef = useRef<HTMLCanvasElement>(null)
  const technologyGapChartRef = useRef<HTMLCanvasElement>(null)
  const futureImpactChartRef = useRef<HTMLCanvasElement>(null)

  // Generate a unique key for this specific report
  const REPORT_ID = "attendee-generated-content-2025"

  return (
    <ReportLayout
      title="Attendee Content Impact Report 2025 - AI Event Data"
      description="Comprehensive 2025 report on attendee-generated content impact. AI-driven insights, ROI metrics & event marketing benchmarks."
      reportId={REPORT_ID}
      reportTitle="Attendee Content Impact Report 2025 - AI Event Data"
      reportSubtitle="Survey Insights from Event Marketing Professionals • 2025"
      keywords={[
        "attendee generated content, event content report 2025, UGC event marketing, attendee content analytics, event social media impact, AI content insights, event engagement metrics, user generated content events, event marketing research, attendee advocacy measurement, social media event ROI, content marketing events, event UGC statistics, attendee engagement data, event content benchmarks",
      ]}
      meta={[
        {
          name: "keywords",
          content:
            "attendee-generated content, event marketing, UGC, event trends 2025, content marketing, event ROI, event technology",
        },
        { name: "robots", content: "index, follow" },
        { name: "author", content: "Premagic" },
        {
          name: "og:title",
          content:
            "The Impact of Attendee-Generated Content on Event Marketing | 2025 Report",
        },
        {
          name: "og:description",
          content:
            "Discover key insights on how attendee-generated content drives event success. Get access to our 2025 survey results revealing critical trends and opportunities.",
        },
        { name: "og:type", content: "article" },
        {
          name: "og:url",
          content:
            "https://premagic.com/reports/attendee-generated-content-impact-report-2025/",
        },
        { name: "twitter:card", content: "summary_large_image" },
        {
          name: "twitter:title",
          content:
            "The Impact of Attendee-Generated Content on Event Marketing | 2025 Report",
        },
        {
          name: "twitter:description",
          content:
            "Discover key insights on how attendee-generated content drives event success. Get access to our 2025 survey results.",
        },
      ]}
    >
      {/* Header Section */}
      <div className="report-header">
        <h1>The Impact of Attendee-Generated Content on Event Marketing</h1>
        <p className="subtitle">
          Survey Insights from Event Marketing Professionals • 2025
        </p>

        <div className="stats-banner">
          <div className="stat-item">
            <span className="stat-number">
              {typedReportData.headerStats.rateUgcImportant}%
            </span>
            <div className="stat-label">Rate UGC as Important</div>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {typedReportData.headerStats.seeReachExtension}%
            </span>
            <div className="stat-label">See Reach Extension</div>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {typedReportData.headerStats.canMeasureROI}%
            </span>
            <div className="stat-label">Can Measure ROI</div>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {typedReportData.headerStats.dontTrackJourneys}%
            </span>
            <div className="stat-label">Don't Track Journeys</div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="content">
        {/* Executive Summary */}
        <div className="section">
          <h2>Executive Summary</h2>
          <p>
            This survey of event marketing professionals reveals both the
            tremendous potential and current challenges in leveraging
            attendee-generated content. While the industry universally
            recognizes UGC's value, significant gaps exist in implementation and
            measurement practices that are limiting the full potential of this
            powerful marketing approach.
          </p>

          <div className="key-findings">
            {typedReportData.executiveSummary.keyFindings.map(
              (finding, index) => (
                <div className="finding-card" key={index}>
                  <div className="finding-number">{finding.number}</div>
                  <div className="finding-text">{finding.text}</div>
                </div>
              )
            )}
          </div>

          <h3>Key Findings for Event Marketers</h3>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>
              <strong>Universal Recognition, Execution Challenges:</strong>{" "}
              While{" "}
              {
                typedReportData.executiveSummary.bulletPoints
                  .universalRecognition
              }
              % see UGC as strategically important, most lack systematic
              approaches to maximize its impact
            </li>
            <li>
              <strong>Measurement Blind Spot:</strong> Only{" "}
              {
                typedReportData.executiveSummary.bulletPoints
                  .measurementConfidence
              }
              % can confidently measure ROI, leaving most event marketers unable
              to prove UGC's business value
            </li>
            <li>
              <strong>Manual Processes Limiting Scale:</strong>{" "}
              {typedReportData.executiveSummary.bulletPoints.manualProcesses}%
              still rely on manual collection, preventing scalable, consistent
              UGC programs
            </li>
            <li>
              <strong>Future Event Impact Untapped:</strong>{" "}
              {
                typedReportData.executiveSummary.bulletPoints
                  .futureRegistrationsUGC
              }
              % believe UGC drives future registrations, yet most cannot
              identify which content performs best
            </li>
          </ul>

          <h3>Strategic Implications for Event Professionals</h3>
          <p>
            The survey reveals that event marketers who develop systematic UGC
            strategies, implement proper measurement frameworks, and invest in
            streamlined processes will gain significant competitive advantages
            in audience engagement, event promotion, and attendee acquisition.
          </p>
        </div>

        {/* Strategic Importance */}
        <div className="section">
          <h2>Strategic Importance & Effectiveness</h2>

          <div className="summary-stats">
            <div className="summary-stat">
              <span className="number">
                {
                  typedReportData.strategicImportance.summaryStats
                    .rateUgcImportant
                }
                %
              </span>
              <div className="label">Rate UGC as Important (4-5/5)</div>
            </div>
            <div className="summary-stat">
              <span className="number">
                {
                  typedReportData.strategicImportance.summaryStats
                    .seeReachExtension
                }
                %
              </span>
              <div className="label">See Effective Reach Extension</div>
            </div>
            <div className="summary-stat">
              <span className="number">
                {
                  typedReportData.strategicImportance.summaryStats
                    .avgEffectivenessRating
                }
              </span>
              <div className="label">Average Effectiveness Rating</div>
            </div>
            <div className="summary-stat">
              <span className="number">
                {
                  typedReportData.strategicImportance.summaryStats
                    .avgImportanceRating
                }
              </span>
              <div className="label">Average Importance Rating</div>
            </div>
          </div>

          <div className="chart-container">
            <h3>Strategic Importance Distribution</h3>
            <div className="chart-wrapper small">
              <canvas ref={importanceChartRef} id="importanceChart"></canvas>
            </div>
          </div>

          <div className="insight-box">
            <h4>🎯 Key Insight for Event Marketers</h4>
            <p>
              Your peers universally recognize UGC's strategic value and have
              seen real results in extending event reach. This validates
              investing time and resources in developing more systematic
              approaches to attendee content initiatives.
            </p>
          </div>
        </div>

        <div className="page-break"></div>

        {/* The Measurement Gap */}
        <div className="section">
          <h2>The Measurement & Tracking Challenge</h2>

          <table className="data-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>High Confidence (4-5/5)</th>
                <th>Low Confidence (1-2/5)</th>
                <th>Opportunity Gap</th>
              </tr>
            </thead>
            <tbody>
              {typedReportData.measurementGap.table.map((row, index) => (
                <tr key={index}>
                  <td>{row.metric}</td>
                  <td>{row.highConfidence}%</td>
                  <td>{row.lowConfidence}%</td>
                  <td>
                    {row.opportunityGap.includes("%") ? (
                      <strong>{row.opportunityGap}</strong>
                    ) : (
                      row.opportunityGap
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="chart-container">
            <h3>The Critical Measurement Gap</h3>
            <div className="chart-wrapper">
              <canvas
                ref={measurementGapChartRef}
                id="measurementGapChart"
              ></canvas>
            </div>
          </div>

          <div className="insight-box">
            <h4>📊 Critical Action Needed</h4>
            <p>
              While you and your peers see UGC's strategic value, most cannot
              prove its business impact. Developing measurement capabilities
              should be a top priority - those who can demonstrate ROI will
              secure more budget and resources for UGC initiatives.
            </p>
          </div>
        </div>

        {/* Content Types & Platforms */}
        <div className="section">
          <h2>Content Types & Platform Preferences</h2>

          <div className="two-column">
            <div>
              <h3>Content Types Actively Promoted</h3>
              <table className="data-table">
                <tbody>
                  <tr>
                    <td>Photos</td>
                    <td>96%</td>
                  </tr>
                  <tr>
                    <td>Social Media Posts</td>
                    <td>88%</td>
                  </tr>
                  <tr>
                    <td>Reviews/Testimonials</td>
                    <td>73%</td>
                  </tr>
                  <tr>
                    <td>Videos</td>
                    <td>58%</td>
                  </tr>
                  <tr>
                    <td>Blog Posts</td>
                    <td>35%</td>
                  </tr>
                  <tr>
                    <td>Audio Content</td>
                    <td>19%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <h3>Primary Collection Platforms</h3>
              <table className="data-table">
                <tbody>
                  <tr>
                    <td>LinkedIn</td>
                    <td>85%</td>
                  </tr>
                  <tr>
                    <td>Instagram</td>
                    <td>77%</td>
                  </tr>
                  <tr>
                    <td>Event Website</td>
                    <td>42%</td>
                  </tr>
                  <tr>
                    <td>Facebook</td>
                    <td>38%</td>
                  </tr>
                  <tr>
                    <td>YouTube</td>
                    <td>31%</td>
                  </tr>
                  <tr>
                    <td>Event App</td>
                    <td>23%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="chart-container">
            <h3>Content Types Distribution</h3>
            <div className="chart-wrapper">
              <canvas
                ref={contentTypesChartRef}
                id="contentTypesChart"
              ></canvas>
            </div>
          </div>

          <div className="chart-container">
            <h3>Platform Usage Distribution</h3>
            <div className="chart-wrapper">
              <canvas ref={platformsChartRef} id="platformsChart"></canvas>
            </div>
          </div>
        </div>

        <div className="page-break"></div>

        {/* Challenges & Solutions */}
        <div className="section">
          <h2>Current Challenges & Desired Solutions</h2>

          <div className="two-column">
            <div>
              <h3>Top Challenges</h3>
              <table className="data-table">
                <tbody>
                  <tr>
                    <td>Content Quality Concerns</td>
                    <td>62%</td>
                  </tr>
                  <tr>
                    <td>Low Participation Rates</td>
                    <td>54%</td>
                  </tr>
                  <tr>
                    <td>Measuring Impact/ROI</td>
                    <td>46%</td>
                  </tr>
                  <tr>
                    <td>Content Moderation</td>
                    <td>35%</td>
                  </tr>
                  <tr>
                    <td>Staff Bandwidth</td>
                    <td>31%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <h3>Most Wanted Improvements</h3>
              <table className="data-table">
                <tbody>
                  <tr>
                    <td>Better Technology Solutions</td>
                    <td>65%</td>
                  </tr>
                  <tr>
                    <td>More Creative Prompts</td>
                    <td>58%</td>
                  </tr>
                  <tr>
                    <td>Best Practice Examples</td>
                    <td>50%</td>
                  </tr>
                  <tr>
                    <td>Improved Measurement Tools</td>
                    <td>42%</td>
                  </tr>
                  <tr>
                    <td>Staff Training</td>
                    <td>38%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="chart-container">
            <h3>Challenges vs. Desired Solutions</h3>
            <div className="chart-wrapper">
              <canvas
                ref={challengesSolutionsChartRef}
                id="challengesSolutionsChart"
              ></canvas>
            </div>
          </div>

          <div className="insight-box">
            <h4>🔧 Actionable Solution Path</h4>
            <p>
              Your biggest challenge (content quality) aligns with what you most
              want (better technology). Event marketers who invest in systematic
              quality control and content standards will see improved
              participation rates and better overall results.
            </p>
          </div>
        </div>

        {/* Technology Gap */}
        <div className="section">
          <h2>The Technology Implementation Gap</h2>

          <h3>Current Tool Usage</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Technology Approach</th>
                <th>Current Usage</th>
                <th>Market Opportunity</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Manual Collection & Curation</td>
                <td>54%</td>
                <td>High automation potential</td>
              </tr>
              <tr>
                <td>No Tools At All</td>
                <td>31%</td>
                <td>Immediate technology need</td>
              </tr>
              <tr>
                <td>Social Media Aggregation</td>
                <td>23%</td>
                <td>Upgrade opportunity</td>
              </tr>
              <tr>
                <td>AI-Powered Management</td>
                <td>19%</td>
                <td>
                  <strong>Massive growth potential</strong>
                </td>
              </tr>
              <tr>
                <td>Event Apps with UGC</td>
                <td>15%</td>
                <td>Integration opportunity</td>
              </tr>
            </tbody>
          </table>

          <div className="chart-container">
            <h3>Technology Adoption vs. Market Demand</h3>
            <div className="chart-wrapper">
              <canvas
                ref={technologyGapChartRef}
                id="technologyGapChart"
              ></canvas>
            </div>
          </div>

          <div className="insight-box">
            <h4>🚀 Competitive Advantage Opportunity</h4>
            <p>
              Most event marketers are still using manual processes while
              wanting better technology. Those who modernize their UGC
              management first will gain significant advantages in efficiency,
              scale, and measurement capabilities over competitors.
            </p>
          </div>

          <h3>Actionable Recommendations for Event Marketers</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Priority Level</th>
                <th>Action Item</th>
                <th>Business Impact</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Immediate (0-3 months)</td>
                <td>Implement basic UGC tracking systems</td>
                <td>Start measuring what currently goes unmeasured</td>
              </tr>
              <tr>
                <td>Short-term (3-6 months)</td>
                <td>Develop content quality standards</td>
                <td>Address #1 challenge cited by 62% of peers</td>
              </tr>
              <tr>
                <td>Medium-term (6-12 months)</td>
                <td>Invest in technology solutions</td>
                <td>Move beyond manual processes like 54% currently use</td>
              </tr>
              <tr>
                <td>Long-term (12+ months)</td>
                <td>Build comprehensive ROI framework</td>
                <td>Join the confident 31% who can prove UGC value</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Future Impact */}
        <div className="section">
          <h2>Impact on Future Events</h2>

          <div className="summary-stats">
            <div className="summary-stat">
              <span className="number">
                {
                  typedReportData.futureImpact.summaryStats
                    .contributeToFutureRegistrations
                }
                %
              </span>
              <div className="label">
                Believe UGC Contributes to Future Registrations
              </div>
            </div>
            <div className="summary-stat">
              <span className="number">
                {
                  typedReportData.futureImpact.summaryStats
                    .influenceNextEventAttendance
                }
                %
              </span>
              <div className="label">
                Agree UGC Influences Next Event Attendance
              </div>
            </div>
            <div className="summary-stat">
              <span className="number">
                {
                  typedReportData.futureImpact.summaryStats
                    .avgFutureImpactRating
                }
              </span>
              <div className="label">Average Future Impact Rating</div>
            </div>
            <div className="summary-stat">
              <span className="number">
                {
                  typedReportData.futureImpact.summaryStats
                    .avgUgcVsOwnContentEffectiveness
                }
              </span>
              <div className="label">
                Average UGC vs. Own Content Effectiveness
              </div>
            </div>
          </div>

          <div className="chart-container">
            <h3>Contribution to Future Registration Rates</h3>
            <div className="chart-wrapper small">
              <canvas
                ref={futureImpactChartRef}
                id="futureImpactChart"
              ></canvas>
            </div>
          </div>
        </div>

        {/* Conclusions & Next Steps */}
        <div className="section">
          <h2>Conclusions & Next Steps for Event Marketers</h2>

          <h3>What This Means for Your Event Strategy</h3>
          <p>
            This survey reveals that you're not alone in recognizing UGC's value
            - your entire industry sees its importance. However, most event
            marketers are struggling with the same implementation and
            measurement challenges you may be facing.
          </p>

          <div className="key-findings">
            <div className="finding-card">
              <div className="finding-number">You're Not Alone</div>
              <div className="finding-text">
                94% of your peers share your belief in UGC's strategic
                importance
              </div>
            </div>
            <div className="finding-card">
              <div className="finding-number">Shared Challenges</div>
              <div className="finding-text">
                Most struggle with the same issues: measurement, quality, and
                manual processes
              </div>
            </div>
            <div className="finding-card">
              <div className="finding-number">Competitive Edge</div>
              <div className="finding-text">
                Those who solve these challenges first will gain significant
                advantages
              </div>
            </div>
          </div>

          <h3>Your Action Plan</h3>
          <ol className="list-decimal pl-5 space-y-2 mt-3">
            <li>
              <strong>Start Measuring Now:</strong> Even basic tracking puts you
              ahead of 73% of peers who can't track UGC conversion journeys
            </li>
            <li>
              <strong>Develop Quality Standards:</strong> Address the #1
              challenge that 62% of event marketers face
            </li>
            <li>
              <strong>Move Beyond Manual:</strong> 54% are still doing manual
              collection - automation provides immediate competitive advantage
            </li>
            <li>
              <strong>Build ROI Proof:</strong> Join the confident 31% who can
              demonstrate UGC's business value
            </li>
          </ol>

          <h3>The Competitive Advantage</h3>
          <p>
            Event marketers who systematically address measurement, quality, and
            process challenges will differentiate themselves significantly. With
            85% seeing proven reach extension but only 31% able to measure ROI,
            there's a clear opportunity to become industry leaders in UGC
            effectiveness.
          </p>
        </div>

        {/* Methodology */}
        <div className="methodology">
          <h3>Survey Methodology</h3>
          <p>
            <strong>Survey Period:</strong> May 2025 | <strong>Method:</strong>{" "}
            Online survey distributed to event marketing professionals |{" "}
            <strong>Participants:</strong> Event marketing professionals across
            various industries including corporate events, conferences,
            entertainment, and trade shows | <strong>Response Format:</strong>{" "}
            Multiple choice questions, 5-point Likert scales, and open-ended
            qualitative responses | <strong>Data Collection:</strong> Google
            Forms with comprehensive validation | <strong>Analysis:</strong>{" "}
            Statistical analysis of quantitative responses with thematic
            analysis of qualitative feedback
          </p>
        </div>

        {/* About Premagic */}
        <div className="section" style={{ marginTop: "40px" }}>
          <h2>About Premagic</h2>

          {/* Hero section */}
          <div
            style={{
              background: "linear-gradient(135deg, #1F0F61 0%, #7251f4 100%)",
              color: "white",
              padding: "30px",
              borderRadius: "12px",
              marginBottom: "30px",
              boxShadow: "0 4px 20px rgba(31, 15, 97, 0.15)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                marginBottom: "15px",
                color: "white",
              }}
            >
              Transforming Event Marketing Through AI
            </h3>
            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                maxWidth: "800px",
                margin: "0 auto 15px",
                color: "white",
              }}
            >
              Premagic is an AI-based platform designed to boost organic event
              marketing by unleashing the power of event attendees. Our mission
              is simple: convert your event attendees into event marketers.
            </p>
            <div
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(5px)",
                padding: "12px 20px",
                borderRadius: "8px",
                marginTop: "10px",
                display: "inline-block",
              }}
            >
              <p style={{ margin: 0, fontWeight: "600", color: "white" }}>
                {typedReportData.aboutPremagic.consumerTrustUGC}% of consumers
                trust user-generated content more than traditional ads
              </p>
            </div>
          </div>

          {/* Feature grid */}
          <h3
            style={{
              fontSize: "20px",
              color: "#1F0F61",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            Key Platform Features
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "20px",
              margin: "0 0 30px",
            }}
          >
            {/* Feature 1 */}
            <div
              style={{
                background: "white",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e9ecef",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              <div
                style={{
                  color: "#7251f4",
                  fontSize: "22px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "10px" }}>📸</span>
                <strong>AI-Based Photo Distribution</strong>
              </div>
              <p style={{ color: "#4d6178", margin: 0 }}>
                Personalized photo galleries delivered instantly to attendees,
                increasing engagement and sharing.
              </p>
            </div>

            {/* Feature 2 */}
            <div
              style={{
                background: "white",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e9ecef",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              <div
                style={{
                  color: "#7251f4",
                  fontSize: "22px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "10px" }}>🆔</span>
                <strong>Face Check-in & Badge Creation</strong>
              </div>
              <p style={{ color: "#4d6178", margin: 0 }}>
                Seamless event registration and security with advanced facial
                recognition technology.
              </p>
            </div>

            {/* Feature 3 */}
            <div
              style={{
                background: "white",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e9ecef",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              <div
                style={{
                  color: "#7251f4",
                  fontSize: "22px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "10px" }}>📊</span>
                <strong>UGC Prompts & Analytics</strong>
              </div>
              <p style={{ color: "#4d6178", margin: 0 }}>
                Automated content encouragement and comprehensive tracking of
                engagement metrics.
              </p>
            </div>

            {/* Feature 4 */}
            <div
              style={{
                background: "white",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e9ecef",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              <div
                style={{
                  color: "#7251f4",
                  fontSize: "22px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "10px" }}>🖼️</span>
                <strong>Digital Asset Management</strong>
              </div>
              <p style={{ color: "#4d6178", margin: 0 }}>
                Store and search photos using face recognition with powerful
                organizational tools.
              </p>
            </div>

            {/* Feature 5 */}
            <div
              style={{
                background: "white",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e9ecef",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              <div
                style={{
                  color: "#7251f4",
                  fontSize: "22px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "10px" }}>🏷️</span>
                <strong>Sponsored Content Integration</strong>
              </div>
              <p style={{ color: "#4d6178", margin: 0 }}>
                Enhanced brand visibility and ROI through strategic content
                placement.
              </p>
            </div>

            {/* Feature 6 */}
            <div
              style={{
                background: "white",
                borderRadius: "10px",
                padding: "20px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e9ecef",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              <div
                style={{
                  color: "#7251f4",
                  fontSize: "22px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "10px" }}>🔄</span>
                <strong>Networking & Event Memories</strong>
              </div>
              <p style={{ color: "#4d6178", margin: 0 }}>
                Building lasting connections and engagement through shared
                experiences.
              </p>
            </div>
          </div>

          {/* Industry Recognition */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "30px",
              marginBottom: "30px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                flex: "1 1 400px",
                background: "#f8f9fa",
                padding: "25px",
                borderRadius: "10px",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e9ecef",
              }}
            >
              <h4
                style={{
                  color: "#1F0F61",
                  marginBottom: "15px",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "10px" }}>🏆</span>
                Industry Recognition
              </h4>
              <p
                style={{
                  fontSize: "14px",
                  lineHeight: "1.6",
                  color: "#4d6178",
                }}
              >
                Recognized by Julius Solaris as one of the "Event Tech Key
                Players 2024" among the biggest event technology solutions
                globally. Premagic has been featured in multiple industry
                publications for innovation in event technology.
              </p>
            </div>

            <div
              style={{
                flex: "1 1 300px",
                background: "#1F0F61",
                color: "white",
                padding: "25px",
                borderRadius: "10px",
                boxShadow: "0 4px 15px rgba(31, 15, 97, 0.2)",
              }}
            >
              <h4
                style={{
                  marginBottom: "15px",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  color: "white",
                }}
              >
                <span style={{ marginRight: "10px" }}>🚀</span>
                Get Started Today
              </h4>
              <p
                style={{
                  fontSize: "14px",
                  marginBottom: "15px",
                  lineHeight: "1.6",
                  color: "white",
                }}
              >
                Ready to transform your event marketing with the power of
                attendee-generated content?
              </p>
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  padding: "15px",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    margin: "0 0 5px",
                    fontWeight: "600",
                    color: "white",
                  }}
                >
                  Contact Us:
                </p>
                <p style={{ margin: "0", fontSize: "16px", color: "white" }}>
                  events@premagic.com
                </p>
                <p style={{ margin: "0", fontSize: "16px", color: "white" }}>
                  www.premagic.com
                </p>
              </div>
            </div>
          </div>

          {/* Trusted clients */}
          <div
            style={{
              background: "white",
              borderRadius: "10px",
              padding: "30px",
              marginBottom: "30px",
              boxShadow: "0 2px 15px rgba(0, 0, 0, 0.05)",
              border: "1px solid #e9ecef",
            }}
          >
            <h3
              style={{
                fontSize: "20px",
                color: "#1F0F61",
                marginBottom: "20px",
                textAlign: "center",
              }}
            >
              Trusted by Leading Event Organizers
            </h3>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "20px",
              }}
            >
              {/* Client logos */}
              {typedReportData.aboutPremagic.clients.map((client, index) => (
                <div
                  key={index}
                  style={{
                    background: "linear-gradient(145deg, #ffffff, #f5f5f5)",
                    padding: "20px 25px",
                    borderRadius: "12px",
                    boxShadow: "5px 5px 15px #e6e6e6, -5px -5px 15px #ffffff",
                    minWidth: "140px",
                    height: "70px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <strong style={{ color: "#1F0F61", fontSize: "16px" }}>
                    {client}
                  </strong>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonials */}
          <div
            style={{
              background: "#f8f9fa",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 2px 15px rgba(0, 0, 0, 0.05)",
              marginTop: "20px",
            }}
          >
            <div
              style={{
                background: "#1F0F61",
                padding: "15px 25px",
                color: "white",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span style={{ marginRight: "10px", fontSize: "24px" }}>💬</span>
              <h4 style={{ margin: 0, fontSize: "18px", color: "white" }}>
                What Our Clients Say
              </h4>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "30px",
                padding: "25px",
              }}
            >
              {typedReportData.aboutPremagic.testimonials.map(
                (testimonial, index) => (
                  <div
                    key={index}
                    style={{
                      background: "white",
                      padding: "25px",
                      borderRadius: "10px",
                      boxShadow: "0 3px 10px rgba(0, 0, 0, 0.05)",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "60px",
                        position: "absolute",
                        top: "10px",
                        left: "15px",
                        color: "#f1f4ff",
                        fontFamily: "serif",
                        zIndex: 0,
                      }}
                    >
                      "
                    </div>
                    <p
                      style={{
                        fontSize: "14px",
                        fontStyle: "italic",
                        color: "#4d6178",
                        position: "relative",
                        zIndex: 1,
                        lineHeight: "1.6",
                        marginBottom: "15px",
                      }}
                    >
                      "{testimonial.quote}"
                    </p>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          background: index % 2 === 0 ? "#1F0F61" : "#7251f4",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontWeight: "bold",
                          marginRight: "12px",
                        }}
                      >
                        {testimonial.initials}
                      </div>
                      <div>
                        <p
                          style={{
                            margin: "0",
                            fontWeight: "600",
                            color: "#1F0F61",
                          }}
                        >
                          {testimonial.author}
                        </p>
                        <p
                          style={{
                            margin: "0",
                            fontSize: "12px",
                            color: "#4d6178",
                          }}
                        >
                          {testimonial.position}, {testimonial.company}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chart initialization component */}
      <AttendeeReportCharts
        importanceChartRef={importanceChartRef}
        measurementGapChartRef={measurementGapChartRef}
        contentTypesChartRef={contentTypesChartRef}
        platformsChartRef={platformsChartRef}
        challengesSolutionsChartRef={challengesSolutionsChartRef}
        technologyGapChartRef={technologyGapChartRef}
        futureImpactChartRef={futureImpactChartRef}
      />
    </ReportLayout>
  )
}

export default AttendeeContentReport
